import { message, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useContext, useMemo } from 'react';

import { useAvatarProStore } from '@/3.0/ai-avatar-generator/store';
import { useImageToVideoStore } from '@/3.0/video-generation/store/imageToVideoStore';
import {
  ImagePreviewCompareImperative,
  ImagePreviewImperative,
} from '@/components/common/ImagePreview/imperative';
import Loading from '@/components/common/Loading';
import MoreMenu from '@/components/common/MoreMenu';
import { VideoPlayerImperative } from '@/components/common/VideoPlayer/imperative';
import {
  MediaItem,
  MediaType,
  TaskState,
} from '@/components/DrawerTaskPanel/useTaskList';
import { RightContext } from '@/components/RightProvider/context';
import { PaymentModal, RenameAssetModal } from '@/components/UIComponents/CommonModal';
import { useRouteChange } from '@/hooks/useRouteChange';
import { downloadTaskFile } from '@/services';
import { deleteTask } from '@/services/common/batchTask';
import { deleteVideo } from '@/services/project';
import { AIFunction, F_CLOUD_TASK_TYPE } from '@/utils/const';
import {
  AssetClickEnum,
  DownloadTier,
  EventNewID,
  StatusEnum,
  SubscribeReasonEnum,
} from '@/utils/eventlog/eventNewConfig';
import {
  getGTMAppName,
  getGTMFeatureName,
  pushGTMEvent,
} from '@/utils/eventlog/gtmDataLayer';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { downloadStreamFile } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { avatarVideoProURL, videoGenerateURL } from '@/utils/routeConstants';
import { PageTabParam } from '@/utils/router';
import { APP_VERSION } from '@/version';

import { MemorizeImage } from '../../../assets/_components/AssetCard';
import AudioPlayer from '../../../common/AudioPlayer';
import { getDownloadFeatureInfo } from '.';
import { ErrorIcon, ProIcon } from './icon';
import styles from './index.module.scss';
import { MenuItem } from './types';

interface MediaPreviewProps {
  items: MediaItem[];
  cloudTaskType: F_CLOUD_TASK_TYPE;
  createdAt?: string;
  onPlay?: (item: MediaItem) => void;
  onDelete?: (item: MediaItem) => void;
  onRename?: (itemId: number | string, newTitle: string) => void;
  onDeleteBatch?: () => void;
  // 自定义布局渲染函数 - 用于实现分类、分组等复杂布局
  renderLayout?: (renderProps: {
    items: MediaItem[];
    renderItem: (item: MediaItem, customMenu?: MenuItem[]) => React.ReactNode;
    getItemMenuBuilders: (item: MediaItem) => any;
    createdAt?: string;
  }) => React.ReactNode;
}

const MaskOverlay: React.FC<{ state?: TaskState; errorMsg?: string }> = ({
  state,
  errorMsg,
}) => {
  if (state === TaskState.PENDING) {
    return (
      <div className={styles.mediaLoadingOverlay}>
        <div className={styles.loadingSpinner}>
          <Loading size={20} />
        </div>
      </div>
    );
  }
  if (state === TaskState.FAILED) {
    return (
      <div className={styles.mediaLoadingOverlay}>
        <div
          className={`${styles.loadingError} flex h-full w-full flex-col items-center justify-center px-[4px] pb-[4px] pt-[30px]`}
        >
          <ErrorIcon />
          <span className="mt-1 line-clamp-3 break-all text-center text-[10px] text-[#FC5132]">
            {errorMsg}
          </span>
        </div>
      </div>
    );
  }
  return null;
};

const PlayButton: React.FC = () => (
  <div className={styles.mediaPlayButton}>
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <circle cx="18" cy="18" r="18" fill="black" fillOpacity="0.24" />
      <path
        d="M25.6475 16.3143C26.8785 17.1008 26.8785 18.8987 25.6475 19.6851L15.9337 25.8911C14.6024 26.7417 12.8569 25.7855 12.857 24.2058L12.857 11.7937C12.857 10.2139 14.6024 9.25775 15.9337 10.1083L25.6475 16.3143Z"
        fill="white"
        fillOpacity="0.9"
      />
    </svg>
  </div>
);

const VideoImagePreviewItem: React.FC<{
  item: MediaItem;
  type: MediaType;
  onDelete?: (item: MediaItem) => void;
  onDeleteBatch?: () => void;
  onRename?: (itemId: number | string, newTitle: string) => void;
  isLastItem?: boolean;
  customMenu?: MenuItem[];
}> = memo(({ item, type, onDelete, onDeleteBatch, onRename, isLastItem, customMenu }) => {
  const router = useRouter();
  const { navigate } = useRouteChange();
  const rightContext = useContext(RightContext);
  const useIdentify = rightContext.getIdentity();
  const customType = +item.rawData?.customType;

  const state = item.state;

  const errorMsg = useMemo(() => {
    if (state === TaskState.NonCompliance) {
      return t('The-material-is-not-compliant');
    }
    if (state === TaskState.FAILED) {
      const rawData = item.rawData as any;

      const noTextErrorCode = [
        'TRANSLATE_INVALID_SRT_FILE',
        'OCR_NOT_FOUNT_SUBTITLES',
        'ERASE_NO_TEXT_DETECTED_IN_CROP_BOX',
      ];
      const noAudioErrorCode = [
        'ALGO_VOICE_DEMUCS_AUDIO_SILENT',
        'WHISPER_AUDIO_FILE_IS_SILENCE',
        'ALGO_VOICE_DEMUCS_NOT_AN_AUIDO_FILE',
      ];
      const langNotSupportErrorCode = ['1232901', 'OCR_INVALID_INPUT_REC_LANG'];

      if (noTextErrorCode.includes(rawData.engineCode || '')) {
        return t('No-text-content-detected');
      }

      if (noAudioErrorCode.includes(rawData.engineCode || '')) {
        return t('No-audio-content-detected');
      }

      if (langNotSupportErrorCode.includes(rawData.engineCode || '')) {
        return t('This-videos-original-language-is-not-supported');
      }

      return t('bc.task.fail.text');
    }
    return undefined;
  }, [state, item.rawData]);

  const downloadFile = useCallback(
    async ({
      download_tier,
      type,
    }: {
      download_tier: DownloadTier;
      type: AssetClickEnum;
    }) => {
      const businessId = (item.rawData as any)?.businessId || '';
      try {
        const { featureName, modelName: model_id } = getDownloadFeatureInfo([item]);

        trackEvent(EventNewID.ASSET_CLICK, {
          source: featureName,
          assetClickType: type,
        });

        trackEvent(EventNewID.CREATIONS_DOWNLOAD, {
          source: featureName,
          model_id,
          download_tier,
          task_id: businessId,
        });

        const gtmDownloadParams: Record<string, string> = {
          app_name: getGTMAppName(featureName),
          feature: getGTMFeatureName(featureName),
          download_tier:
            download_tier === DownloadTier.PAID ? 'no_watermark' : 'with_watermark',
        };
        if (model_id) {
          gtmDownloadParams.model = model_id;
        }
        pushGTMEvent('creation_download', gtmDownloadParams as any);
        const res = await downloadTaskFile([item.id]);
        if (res.code === 200) {
          let result = res.data || [];
          let array = result.map((down: any) => {
            return {
              fileUrl: down.fileUrl,
              fileName: item.title || Date.now().toString(),
            };
          });
          downloadStreamFile(array);
          trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
            result: StatusEnum.SUCCESS,
            task_id: businessId,
          });
        } else {
          message.error(t('Download-Error'));
          trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
            result: StatusEnum.FAIL,
            task_id: businessId,
          });
        }
      } catch {
        message.error(t('Download-Error'));
        trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
          result: StatusEnum.FAIL,
          task_id: businessId,
        });
      }
    },
    [item.id],
  );

  const renameFile = useCallback(async () => {
    RenameAssetModal.show({
      id: item.id,
      title: item.title,
      onOk(newTitle: string) {
        // 通知父组件更新 UI，传递 itemId 和新标题
        onRename?.(item.id, newTitle);
      },
    });
  }, [item.id, item.title, onRename]);

  const deleteFile = useCallback(async () => {
    try {
      if (isLastItem) {
        // 直接删除批次
        // res = await deleteBatch({ parentId: item.id });
        onDeleteBatch?.();
        return;
      }
      let res;
      if (item.state === TaskState.SUCCESS) {
        // 成功调用删除资产
        res = await deleteVideo([item.id as number]);
      } else {
        // 失败调用删除任务
        res = await deleteTask([item.id as number]);
      }
      if (res.success) {
        message.success(t('merror.tip.deleteSuccess'));
        // 如果是最后一个 item，调用批次删除
        if (isLastItem) {
          onDeleteBatch?.();
        } else {
          onDelete?.(item);
        }
      } else {
        message.error(t('merror.tip.deleteError'));
      }
    } catch (error) {}
  }, [item, isLastItem, onDeleteBatch, onDelete]);

  // 菜单项构建器 - 类似 TaskCard 的设计
  const menuItemBuilders = useMemo(() => {
    const success = state === TaskState.SUCCESS;

    return {
      downloadWithWatermark:
        success && useIdentify === 'freeVip'
          ? {
              key: 'download-with',
              label: t('1fa392999e75851a4964321135e24fa8'),
              onClick: () => {
                downloadFile({
                  download_tier: DownloadTier.FREE,
                  type: AssetClickEnum.download_with_watermark,
                });
              },
            }
          : null,

      downloadWithoutWatermark:
        success && useIdentify === 'freeVip'
          ? {
              key: 'download-without',
              label: (
                <span className="flex justify-center">
                  {t('6a804d250c194110364bbba5a76585de')}&nbsp;
                  <ProIcon />
                </span>
              ),
              onClick: () => {
                const { featureName } = getDownloadFeatureInfo([item]);
                trackEvent(EventNewID.ASSET_CLICK, {
                  source: featureName,
                  assetClickType: AssetClickEnum.download_no_watermark,
                });
                pushGTMEvent('view_pricing_plans', {
                  trigger_type: 'usage_limit',
                  app_name: getGTMAppName(featureName),
                  app_version: APP_VERSION,
                  feature: getGTMFeatureName(featureName),
                  usage_limit: 'no_watermark_download',
                });
                PaymentModal.show({
                  feature: featureName,
                  reason: SubscribeReasonEnum.noWatermarkDownload,
                });
              },
            }
          : null,

      download:
        success && useIdentify !== 'freeVip'
          ? {
              key: 'download',
              label: t('merror.operate.download'),
              onClick: () =>
                downloadFile({
                  download_tier: DownloadTier.PAID,
                  type: AssetClickEnum.download,
                }),
            }
          : null,
      rename: success
        ? {
            key: 'rename',
            label: t('merror.operate.rename'),
            onClick: () => {
              const { featureName } = getDownloadFeatureInfo([item]);
              trackEvent(EventNewID.ASSET_CLICK, {
                source: featureName,
                assetClickType: AssetClickEnum.rename,
              });
              renameFile();
            },
          }
        : null,
      delete: {
        key: 'delete',
        label: t('Delete'),
        danger: true,
        onClick: () => {
          const { featureName } = getDownloadFeatureInfo([item]);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.delete,
          });
          deleteFile();
        },
      },
      useToAIVideo: {
        key: 'use-to-ai-video',
        label: t('fc.8a5448b4'),
        onClick: () => {
          const { featureName } = getDownloadFeatureInfo([item]);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.go_to_aivideo,
          });
          const { id, rawData, title } = item;
          const { fileUrl } = rawData || {};
          useImageToVideoStore.getState().setExtraData({
            assetsId: id,
            assetsUrl: fileUrl,
            assetsTitle: title,
          });
          navigate(() => {
            router.replace(videoGenerateURL + `?tab=${PageTabParam.IMAGE_TO_VIDEO}`);
          });
        },
      },
      useToAvatar2: {
        key: 'use-to-avatar2',
        label: t('fc.17afe20d'),
        onClick: () => {
          const { id, rawData, title } = item;
          const { fileUrl } = rawData || {};
          const { featureName } = getDownloadFeatureInfo([item]);
          trackEvent(EventNewID.ASSET_CLICK, {
            source: featureName,
            assetClickType: AssetClickEnum.go_to_avatarvideo,
          });
          useAvatarProStore.getState().setAssetsInfo({
            assetsId: id,
            assetsUrl: fileUrl,
            assetsTitle: title,
          });
          navigate(() => {
            router.replace(avatarVideoProURL);
          });
        },
      },
    };
  }, [state, useIdentify, downloadFile, deleteFile]);

  // 最终使用的菜单
  const moreMenuItems = useMemo(() => {
    // 如果传入了自定义菜单，直接使用
    if (customMenu) {
      return customMenu;
    }

    // 否则使用默认菜单
    if (state === TaskState.PENDING) {
      return [];
    }

    if (state === TaskState.FAILED) {
      return [menuItemBuilders.delete];
    }

    // SUCCESS 状态
    return [
      menuItemBuilders.downloadWithWatermark,
      menuItemBuilders.downloadWithoutWatermark,
      menuItemBuilders.download,
      F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE === customType && menuItemBuilders.useToAIVideo,
      F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE === customType && menuItemBuilders.useToAvatar2,
      menuItemBuilders.rename,
      menuItemBuilders.delete,
    ].filter(Boolean) as MenuItem[];
  }, [state, menuItemBuilders, customMenu]);

  const handleClick = () => {
    if (state !== TaskState.SUCCESS) return;

    const { featureName, modelName } = getDownloadFeatureInfo([item]);
    const previewParams: Record<string, string> = {
      app_name: getGTMAppName(featureName),
      feature: getGTMFeatureName(featureName),
    };
    if (modelName) {
      previewParams.model = modelName;
    }
    pushGTMEvent('creation_preview', previewParams as any);

    // 图片高清预览
    if (customType === F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE) {
      ImagePreviewCompareImperative.show({
        src: item.url,
        beforeImgUrl: item.rawData?.extendInfo?.originImgUrl,
        title: item.title,
      });
      return;
    }
    if (type === MediaType.VIDEO) {
      VideoPlayerImperative.show({
        src: item.url,
        title: item.title,
      });
    } else if (type === MediaType.IMAGE) {
      ImagePreviewImperative.show({
        src: item.url,
        title: item.title,
      });
    }
  };

  const imageCover = useMemo(() => {
    const { cover, url } = item;
    if (customType === F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE) {
      return cover || url;
    }

    if (type === MediaType.PENDING || type === MediaType.VIDEO) {
      return cover;
    }

    return url;
  }, [item.cover, item.url, item.rawData?.customType, type]);

  const boxClass = `${styles.mediaPreviewItem} ${state === TaskState.SUCCESS ? styles.clickable : ''} group`;

  return (
    <Tooltip
      title={item.title}
      placement="top"
      color="#636ef4"
      classNames={{
        body: 'text-[12px]',
      }}
    >
      <div className={boxClass} onClick={handleClick}>
        <MemorizeImage cover={imageCover} className="object-cover" />

        <MaskOverlay state={item.state} errorMsg={errorMsg} />
        {state === TaskState.SUCCESS && type === MediaType.VIDEO && <PlayButton />}
        {state !== TaskState.PENDING && (
          <div className="absolute right-[6px] top-[6px] z-[10] opacity-0 transition-all group-hover:opacity-100">
            <MoreMenu menu={moreMenuItems} icon={MoreIcon} />
          </div>
        )}
      </div>
    </Tooltip>
  );
});

VideoImagePreviewItem.displayName = 'VideoImagePreviewItem';

const AudioPreviewItem: React.FC<{
  item: MediaItem;
}> = memo(({ item }) => {
  return (
    <div className={styles.audioPreviewItem}>
      <Tooltip
        title={item.title}
        color="#636ef4"
        classNames={{
          body: 'text-[12px]',
        }}
      >
        <div>
          <AudioPlayer
            fileUrl={item.rawData?.fileUrl || ''}
            duration={item.duration || 0}
            state={item.state}
          />
        </div>
      </Tooltip>
    </div>
  );
});

AudioPreviewItem.displayName = 'AudioPreviewItem';

export const MediaPreview: React.FC<MediaPreviewProps> = memo(
  ({
    cloudTaskType,
    items,
    onDelete,
    createdAt,
    onDeleteBatch,
    onRename,
    renderLayout,
  }) => {
    const rightContext = useContext(RightContext);
    const useIdentify = rightContext.getIdentity();

    const mediaType = useMemo(() => {
      const audioTypes = [
        F_CLOUD_TASK_TYPE.F_VOICE_CLONE,
        F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH,
      ];
      if (audioTypes.includes(cloudTaskType)) {
        return 'audio';
      }
      // 默认返回视频/图片类型
      return 'video_image';
    }, [cloudTaskType]);

    const totalItemsCount = items.length;

    // 封装 renderItem 函数 - 支持自定义菜单
    const renderItem = useCallback(
      (item: MediaItem, customMenu?: MenuItem[]) => {
        return (
          <VideoImagePreviewItem
            key={item.id}
            item={item}
            type={item.mediaType}
            onDelete={onDelete}
            onDeleteBatch={onDeleteBatch}
            onRename={onRename}
            isLastItem={totalItemsCount === 1}
            customMenu={customMenu}
          />
        );
      },
      [totalItemsCount, onDelete, onDeleteBatch, onRename],
    );

    // 获取某个 item 的菜单构建器
    const getItemMenuBuilders = useCallback(
      (item: MediaItem) => {
        const canDownload = item.state === TaskState.SUCCESS;

        // 这里的逻辑需要和 VideoImagePreviewItem 保持一致
        // 但由于我们无法访问具体的 downloadFile/deleteFile 函数
        // 所以返回的是「数据」而非「完整的菜单项」
        // 实际上这个功能主要用于让外部知道「哪些菜单项可用」
        return {
          downloadWithWatermark: canDownload && useIdentify === 'freeVip' ? {} : null,
          downloadWithoutWatermark: canDownload && useIdentify === 'freeVip' ? {} : null,
          download: canDownload && useIdentify !== 'freeVip' ? {} : null,
          delete: {},
        } as any; // 这里类型不完全匹配，但够用了
      },
      [useIdentify],
    );

    if (mediaType === 'audio') {
      const audioItem = items[0];
      if (!audioItem) return null;

      return (
        <div className={styles.mediaPreviewContainer}>
          <AudioPreviewItem item={audioItem} />
        </div>
      );
    }

    // 如果提供了自定义布局，使用它
    if (renderLayout) {
      return (
        <div className={styles.mediaPreviewContainer}>
          {renderLayout({
            items,
            renderItem,
            getItemMenuBuilders,
            createdAt,
          })}
        </div>
      );
    }

    // 默认布局 (现有逻辑)
    return (
      <div className={styles.mediaPreviewContainer}>
        <div className={styles.mediaGrid}>{items.map((item) => renderItem(item))}</div>
      </div>
    );
  },
);

MediaPreview.displayName = 'MediaPreview';

const MoreIcon = (
  <div className="p-[2px] rounded-[6px] bg-[#00000066]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M9.39063 10C9.39063 9.66345 9.66345 9.39063 10 9.39063C10.3365 9.39063 10.6094 9.66345 10.6094 10C10.6094 10.3365 10.3365 10.6094 10 10.6094C9.66345 10.6094 9.39063 10.3365 9.39063 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
      <path
        d="M14.0625 10C14.0625 9.66345 14.3353 9.39063 14.6719 9.39063C15.0084 9.39063 15.2813 9.66345 15.2813 10C15.2813 10.3365 15.0084 10.6094 14.6719 10.6094C14.3353 10.6094 14.0625 10.3365 14.0625 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
      <path
        d="M4.71875 10C4.71875 9.66345 4.99158 9.39063 5.32812 9.39063C5.66467 9.39063 5.9375 9.66345 5.9375 10C5.9375 10.3366 5.66467 10.6094 5.32812 10.6094C4.99158 10.6094 4.71875 10.3366 4.71875 10Z"
        stroke="white"
        strokeWidth="1.21875"
      />
    </svg>
  </div>
);
